import { FileIcon, LinkIcon, LocationIcon } from '@/assets/icons'
import Tooltip from '@components/Tooltip'
import { EventCategory } from '@orbit/api-types'
import { Event } from '@types'
import dayjs from 'dayjs'
import React from 'react'
import {
	CardContainer,
	CardHeader,
	CardTitle,
	CategoryPill,
	IconButton,
	IconRow,
	PillRow,
	TimePill,
} from './Card.styles'

export interface CardContentProps {
	event: Event
	isDragging?: boolean
	isOver?: boolean
	onClick?: (e: React.MouseEvent) => void
	onMouseDown?: (e: React.MouseEvent) => void
	containerRef?: React.Ref<HTMLDivElement>
	containerProps?: React.HTMLAttributes<HTMLDivElement>
}

const formatTime = (timeString: string): string => {
	if (!timeString) return ''
	try {
		return dayjs(`2000-01-01T${timeString}`).format('h:mm A')
	} catch {
		return timeString
	}
}

const CATEGORY_LABELS: Record<EventCategory, string> = {
	FoodAndDrink: 'Food & Drink',
	SeeAndDo: 'See & Do',
	Nature: 'Nature',
	Shopping: 'Shopping',
	Accommodation: 'Accommodation',
	Transportation: 'Transportation',
	Event: 'Event',
	Other: 'Other',
}

const DescriptionGlyph: React.FC = () => (
	<svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
		<line x1="4" y1="7" x2="20" y2="7" />
		<line x1="4" y1="12" x2="20" y2="12" />
		<line x1="4" y1="17" x2="14" y2="17" />
	</svg>
)

const CardContent: React.FC<CardContentProps> = ({
	event,
	isDragging = false,
	isOver = false,
	onClick,
	onMouseDown,
	containerRef,
	containerProps = {},
}) => {
	const hasLocation = !!event.location?.primaryText
	const linkCount = event.tripEventLinks?.length ?? 0
	const attachmentCount = event.fileIds?.length ?? 0
	const hasDescription = !!event.description?.trim()
	const showIconRow = hasLocation || linkCount > 0 || attachmentCount > 0 || hasDescription
	const categoryLabel = event.category ? CATEGORY_LABELS[event.category as EventCategory] : CATEGORY_LABELS.Other
	const showBottomRow = !!event.startTime || showIconRow

	return (
		<CardContainer
			ref={containerRef}
			$isDragging={isDragging}
			$isOver={isOver}
			onMouseDown={onMouseDown}
			onClick={onClick}
			{...containerProps}
		>
			<PillRow>
				<CategoryPill>{categoryLabel}</CategoryPill>
			</PillRow>

			<CardHeader>
				<CardTitle>{event.name}</CardTitle>
			</CardHeader>

			{showBottomRow && (
				<IconRow onClick={e => e.stopPropagation()}>
					{event.startTime && <TimePill>{formatTime(event.startTime)}</TimePill>}
					{hasLocation && (
						<Tooltip text={`Location: ${event.location?.primaryText}`}>
							<IconButton aria-label="Has location">
								<LocationIcon sx={{ fontSize: 16 }} />
							</IconButton>
						</Tooltip>
					)}
					{linkCount > 0 && (
						<Tooltip text={`This event has ${linkCount} link${linkCount === 1 ? '' : 's'}`}>
							<IconButton aria-label="Has links">
								<LinkIcon sx={{ fontSize: 16 }} />
							</IconButton>
						</Tooltip>
					)}
					{attachmentCount > 0 && (
						<Tooltip text={`This event has ${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`}>
							<IconButton aria-label="Has attachments">
								<FileIcon sx={{ fontSize: 16 }} />
								<span>{attachmentCount}</span>
							</IconButton>
						</Tooltip>
					)}
					{hasDescription && (
						<Tooltip text="This event has a description">
							<IconButton aria-label="Has description">
								<DescriptionGlyph />
							</IconButton>
						</Tooltip>
					)}
				</IconRow>
			)}
		</CardContainer>
	)
}

export default CardContent
